/**
 * Content Orders Page Loader
 * Uses the unified /v1/orders/my-orders endpoint with content filter.
 * Response contains both standalone content orders (type: "order") and
 * content packages (type: "package") with nested child orders.
 */

import type { LoaderFunctionArgs } from 'react-router-dom';
import { requireAuth, getSearchParams, loaderCache } from './helpers';
import {
  fetchMyOrders,
  type MyOrderItem,
  type MyPackageItem,
  type MyOrdersParams,
} from '@/services/orderService';
import type {
  ContentOrder,
  ContentPackageOrder,
} from '@/components/content-orders/types';
import {
  trackRouteLoad,
  trackRouteError,
  startRouteTimer,
  calculateDataSize,
} from '@/utils/route-analytics';

export interface ContentOrdersData {
  orders: ContentOrder[];
  packages: ContentPackageOrder[];
  stats: {
    totalOrders: number;
    inProgress: number;
    completed: number;
    totalWords: number;
    averageRating: number;
  };
  filters: {
    statuses: string[];
    types: string[];
    writers: string[];
  };
  recentBriefs: Array<{
    id: string;
    title: string;
    createdAt: string;
  }>;
}

/** Map a generic MyOrderItem (content order_type) into our ContentOrder UI type */
function mapOrderItemToContentOrder(o: MyOrderItem): ContentOrder {
  return {
    id: o.id,
    order_number: o.order_number,
    order_type: 'content',
    title: o.title || '',
    status: o.status,
    progress_pct: Number(o.progress_pct) || 0,
    priority: o.priority || 'normal',
    word_count: Number(o.content_word_count) || 0,
    content_brief: o.content_brief ?? null,
    keywords:
      (o as MyOrderItem & { keywords?: string[] | null }).keywords ?? null,
    target_url: o.target_url ?? null,
    anchor_text: o.anchor_text ?? null,
    content_by:
      (o as MyOrderItem & { content_by?: string | null }).content_by ?? null,
    content_status:
      o.content_writer?.content_status ??
      o.content_task?.content_status ??
      null,
    written_content:
      o.content_writer?.written_content ??
      o.content_task?.written_content ??
      null,
    content_task_id: o.content_task_id ?? o.content_task?.id ?? null,
    advertiser_content: o.advertiser_content ?? null,
    grey_niche: o.grey_niche ?? null,
    grey_niche_id: o.grey_niche_id ?? null,
    total_amount: Number(o.total_amount) || 0,
    writer_payout:
      Number(
        (o as MyOrderItem & { publisher_payout?: string | number })
          .publisher_payout
      ) || 0,
    platform_fee:
      Number(
        (o as MyOrderItem & { platform_fee?: string | number }).platform_fee
      ) || 0,
    currency: o.currency || 'USD',
    payment_status: o.payment_status,
    paid_at: (o as MyOrderItem & { paid_at?: string | null }).paid_at ?? null,
    escrow_id:
      (o as MyOrderItem & { escrow_id?: string | null }).escrow_id ?? null,
    advertiser_notes: o.advertiser_notes ?? null,
    due_date: o.due_date ?? null,
    confirmed_at: o.confirmed_at ?? null,
    started_at: o.started_at ?? null,
    expected_at:
      (o as MyOrderItem & { expected_at?: string | null }).expected_at ?? null,
    completed_at: o.completed_at ?? null,
    cancelled_at: o.cancelled_at ?? null,
    revision_count:
      Number((o as MyOrderItem & { revision_count?: number }).revision_count) ||
      0,
    revisions_allowed:
      (o as MyOrderItem & { revisions_allowed?: boolean }).revisions_allowed ??
      true,
    timeline_events: [],
    contents: o.contents ?? [],
    site: o.site ?? null,
    created_at: o.created_at,
    updated_at: o.updated_at,
  };
}

/** Map a package row into ContentPackageOrder */
function mapPackageItemToContentPackage(
  pkg: MyPackageItem
): ContentPackageOrder {
  const subOrders: ContentOrder[] = (pkg.orders ?? []).map(
    mapOrderItemToContentOrder
  );
  return {
    id: pkg.id,
    order_number: pkg.order_number ?? `PKG-${pkg.id.slice(0, 8).toUpperCase()}`,
    package_name: pkg.package_name,
    _kind: 'package',
    status: pkg.status,
    total_amount: Number(pkg.final_price) || 0,
    payment_status: pkg.payment_status ?? 'paid',
    due_date: pkg.expected_completion_date ?? null,
    created_at: pkg.created_at,
    items: pkg.items,
    subOrders,
  };
}

function buildParams(searchParams: URLSearchParams): MyOrdersParams {
  const params: MyOrdersParams = {
    order_type: 'content',
    package_type: 'content',
    sort_by: searchParams.get('sort_by') ?? 'created_at',
    sort_order: (searchParams.get('sort_order') as 'asc' | 'desc') || 'desc',
    per_page: Number(searchParams.get('per_page')) || 20,
    page: Number(searchParams.get('page')) || 1,
  };

  const pass = ['status', 'date_from', 'date_to', 'search'] as const;
  for (const key of pass) {
    const v = searchParams.get(key);
    if (v) (params as Record<string, unknown>)[key] = v;
  }
  if (searchParams.get('has_available_slots') === 'true') {
    params.has_available_slots = true;
  }
  return params;
}

async function fetchContentOrdersData(
  searchParams: URLSearchParams
): Promise<ContentOrdersData> {
  const cacheKey = `content-orders-${searchParams.toString()}`;
  const cached = loaderCache.get<ContentOrdersData>(cacheKey);
  if (cached) return cached;

  try {
    const response = await fetchMyOrders(buildParams(searchParams));
    const rows = response.data ?? [];

    const orders: ContentOrder[] = [];
    const packages: ContentPackageOrder[] = [];
    for (const row of rows) {
      if (row.type === 'package') {
        packages.push(mapPackageItemToContentPackage(row));
      } else {
        orders.push(mapOrderItemToContentOrder(row));
      }
    }

    // All orders including package children, for stats
    const allOrders = [...orders, ...packages.flatMap((p) => p.subOrders)];

    const data: ContentOrdersData = {
      orders,
      packages,
      stats: {
        totalOrders: response.meta?.total ?? rows.length,
        inProgress: allOrders.filter((o) => o.status === 'in_progress').length,
        completed: allOrders.filter((o) => o.status === 'completed').length,
        totalWords: allOrders.reduce((sum, o) => sum + o.word_count, 0),
        averageRating: 0,
      },
      filters: {
        statuses: [
          'pending',
          'confirmed',
          'in_progress',
          'content_ready',
          'revision',
          'completed',
        ],
        types: ['content'],
        writers: [],
      },
      recentBriefs: [],
    };

    loaderCache.set(cacheKey, data, 2 * 60 * 1000);
    return data;
  } catch (error) {
    console.error(
      '[contentOrdersLoader] Failed to fetch content orders:',
      error
    );
    return {
      orders: [],
      packages: [],
      stats: {
        totalOrders: 0,
        inProgress: 0,
        completed: 0,
        totalWords: 0,
        averageRating: 0,
      },
      filters: {
        statuses: [
          'pending',
          'confirmed',
          'in_progress',
          'content_ready',
          'revision',
          'completed',
        ],
        types: ['content'],
        writers: [],
      },
      recentBriefs: [],
    };
  }
}

export async function contentOrdersLoader(args: LoaderFunctionArgs) {
  const getElapsed = startRouteTimer();
  try {
    await requireAuth(args.request);
    const searchParams = getSearchParams(args);
    const data = await fetchContentOrdersData(searchParams);
    const loadTime = getElapsed();
    const dataSize = calculateDataSize(data);
    trackRouteLoad('/content-orders', loadTime, dataSize);
    return data;
  } catch (error) {
    if (error instanceof Response) throw error;
    trackRouteError('/content-orders', error as Error);
    throw error;
  }
}
